/**
 * Well-known INFRASTRUCTURE tenant ids.
 *
 * Lives in `@papan/types` — dependency-free and browser-safe — because both
 * sides need it and there must be exactly ONE definition. `@papan/auth` (server:
 * the projection, the guards, `requirePlatformAdmin`) re-exports these rather
 * than declaring its own, and the SPA imports them directly. `shared/core-ui`
 * may not import `@papan/auth` at all (that boundary is one-way), which is why
 * the constant could not simply stay there.
 *
 * ALWAYS key authority off the ID, NEVER off the display name. Org creation is
 * self-service and names are not unique, so a name-keyed check ("is the caller
 * in an org called 'Papan Admins'?") is a trivial privilege escalation — any
 * user could create their own — and it breaks the moment someone renames the
 * org. The names below are presentation only.
 */

/** Well-known tenant ID for the platform-admin org ("Papan Admins"). */
export const PLATFORM_ADMIN_ORG_ID = 'o_platform_admins'

/** Display name. Presentation only — never an authority check. */
export const PLATFORM_ADMIN_ORG_NAME = 'Papan Admins'

/** Well-known tenant ID for the dev org. */
export const DEV_ORG_TENANT_ID = 'o_developers'

/**
 * Is `tenantId` the platform-admin org?
 *
 * A named predicate rather than an inline `===` so the SPA's "should I show the
 * platform scope?" and the server's `requirePlatformAdmin` are visibly the same
 * question. They are NOT the same authority — the server is the only enforcement
 * point and re-checks `platformRole` besides — but they must agree on which
 * tenant counts, or the UI offers a scope every request then refuses.
 */
export function isPlatformAdminOrg(tenantId: string | null | undefined): boolean {
  return tenantId === PLATFORM_ADMIN_ORG_ID
}

/**
 * Tenants that are INFRASTRUCTURE, not user data: they are re-upserted on every
 * boot and other subsystems address them by fixed id.
 *
 * `o_platform_admins` because renaming or deleting it destroys platform
 * administration. `o_developers` for the same structural reason — it owns
 * `_reports/sessions/` and is addressed by id from the agent session routes.
 *
 * Deleting either would leave the boot seed re-creating an EMPTY org: the
 * tenant row would come back, every membership inside it would not.
 *
 * Moved here from `@papan/auth` when the org-rename UI shipped, because
 * `shared/core-ui` may not import `@papan/auth` and the SPA needs this answer:
 * it no longer HIDES the rename control (the route's rename refusal was removed
 * — see `isSystemTenant` below), but it does put a confirmation in front of
 * renaming one of these. `@papan/auth` re-exports these, so there is still
 * exactly one definition.
 */
export const SYSTEM_TENANT_IDS: ReadonlySet<string> = new Set([
  PLATFORM_ADMIN_ORG_ID,
  DEV_ORG_TENANT_ID,
])

/**
 * Is this tenant protected from DELETION through the normal org routes?
 *
 * DELETION ONLY. Renaming these is allowed (decided 2026-08-18): it breaks no
 * authority — everything addresses them by ID — and blocking it only hid the
 * fact that `PLATFORM_ADMIN_ORG_NAME` puts our brand in every whitelabel
 * deployment's org switcher. Deleting is the irreversible one: the boot seed
 * restores the tenant row but NOT its memberships.
 */
export function isSystemTenant(tenantId: string | null | undefined): boolean {
  return !!tenantId && SYSTEM_TENANT_IDS.has(tenantId)
}

